import React, { Fragment, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  SUCCESS_API_GET_REPORT_DETAIL,
  RESET_REPORT_DETAILS,
  SUCCESS_API_UPDATE_REPORT_STATUS,
} from '../../actions/actionTypes';
import {
  MainBlock,
  BodyBlock,
  ReportTableWrapper,
  FramesWrapper,
} from './styles/ReportEditPageStyles';
import { convertDateToISO8601, isGuest, showToast } from '../../lib/tools';
import PlusCircleButton from '../Parts/PlusCircleButton';
import EditArea from './EditArea';
import TitleArea, { ReportStatus } from './TitleArea';
import {
  PAGE_PATH,
  REPORT_UPDATE_ERROR_CODE,
  SELECT_MEDIA_DIALOG_MODE,
} from '../../lib/constants';
import Frame from './Frame';
import { createReportNo } from '../../lib/reportTools';
import useReportStatus from './hooks/useReportStatus';
import ConfirmationDialog from '../Parts/ConfirmationDialog';
import ColorSF2 from '../../styles/common/ColorSF2';
import SelectMediaDialog from './SelectMediaDialog';
import deleteReportFrame from '../../actions/api/report/deleteReportFrame';
import getReportDetail from '../../actions/api/report/getReportDetail';
import useAutoSave from './hooks/useAutoSave';
import updateReport from '../../actions/api/report/updateReport';
import useBlockNavigation from './hooks/useBlockNavigation';
import updateReportFrame from '../../actions/api/report/updateReportFrame';
import convertTimelineToApiParams from './functions/convertTimelineToApiParams';
import checkReportStatus from './functions/checkReportStatus';

const getDialogTexts = formatMessage => {
  const headerText = formatMessage({
    id: 'report.edit.conflictDialog.title',
  });

  const messageText = (
    <>
      <FormattedMessage id="report.edit.conflictDialog.message1" />
      <br />
      <FormattedMessage id="report.edit.conflictDialog.message2" />
      <br />
      <FormattedMessage id="report.edit.conflictDialog.message3" />
    </>
  );

  return { headerText, messageText };
};

/**
 * Web報告書作成・編集画面のページコンポーネント
 * TODO: カスタムフックにロジックを移動する
 * @component
 */
const ReportEditPage = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();

  const [showSelectMediaDialog, setShowSelectMediaDialog] = useState(false);

  const apiToken = useSelector(state => state.main.apiToken);
  const targetGroupId = useSelector(state => state.main.targetGroupId);
  const reportDetail = useSelector(state => state.report.reportDetail);
  const reportNo = createReportNo(
    reportDetail.groupNo,
    reportDetail.reportNo,
    reportDetail.status,
  );
  const innerRole = useSelector(state => state.main.innerRole);

  // 報告書情報。編集中の情報を保持する。
  const [reportInfo, setReportInfo] = useState({
    reportId: reportDetail.id,
    updatedAt: reportDetail.updatedAt,
    reportNo: reportNo ?? '',
    author: reportDetail.createdOperatorName,
    subject: reportDetail.name ?? '',
    createDate: reportDetail.createdAt ?? '',
    reportStatus: reportDetail.status,
  });

  useEffect(() => {
    if (reportDetail.id != null) {
      setReportInfo({
        reportId: reportDetail.id,
        updatedAt: reportDetail.updatedAt,
        reportNo: reportNo ?? '',
        author: reportDetail.createdOperatorName,
        subject: reportDetail.name ?? '',
        createDate: reportDetail.createdAt ?? '',
        reportStatus: reportDetail.status,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportDetail]);

  // 報告書フレーム
  const getDefaultFrames = () =>
    reportDetail?.frames?.length > 0
      ? reportDetail.frames
      : [
          {
            workRecord: null,
            tags: [],
            note: null,
          },
        ];

  const [frames, setFrames] = useState(getDefaultFrames);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const [prevFrameLength, setPrevFrameLength] = useState(0);
  // APIから取得したフレーム情報をstateに反映。現在入力中のフレーム情報は更新しない。
  useEffect(() => {
    if (reportDetail.id == null) {
      return;
    }

    if (
      isFirstRender ||
      prevFrameLength !== reportDetail.frames.length ||
      !frames.length
    ) {
      setIsFirstRender(false);
      setPrevFrameLength(reportDetail.frames.length);
      if (reportDetail.frames.length !== 0) {
        setFrames(getDefaultFrames());
      }
      return;
    }

    if (reportDetail.frames.length <= 0) {
      return;
    }

    setFrames(prevFrames =>
      reportDetail.frames.map(newFrame => {
        const existingFrame = prevFrames.find(
          frame => frame?.id === newFrame.id,
        );
        if (existingFrame?.workRecord !== newFrame?.workRecord) {
          return newFrame;
        }
        if (
          existingFrame &&
          JSON.stringify(existingFrame) === JSON.stringify(newFrame)
        ) {
          return newFrame;
        }
        return existingFrame;
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportDetail]);

  // 自動保存中にボタンを非活性にするためのフラグ。フレーム毎に自動保存状態を持つため、オブジェクトで管理。
  const [isAutoSaving, setIsAutoSaving] = useState({
    isReportAutoSaving: false, // 件名、作成日時の自動保存中
  });

  const updateAutoSaveStatus = (key, value) => {
    setIsAutoSaving(prevState => ({
      ...prevState,
      [key]: value,
    }));
  };

  // 報告書ヘッダー自動保存処理
  const {
    setReportTitle,
    setCreateDate,
    isSaving,
    showReportNameConflictDialog,
    setShowReportNameConflictDialog,
    showCreatedDateConflictDialog,
    setShowCreatedDateConflictDialog,
    debouncedTitle,
    debouncedDate,
  } = useAutoSave(
    apiToken,
    targetGroupId,
    reportInfo.reportId,
    reportInfo.updatedAt,
    updateAutoSaveStatus,
  );

  useBlockNavigation(isSaving);

  // 報告書ステータスの選択肢、確認ダイアログ情報
  const { reportStatusSelections, statusUpdateConfirmationInfo } =
    useReportStatus(
      reportDetail.status,
      reportInfo.reportStatus,
      formatMessage,
    );

  // 報告書ステータス更新確認ダイアログ表示
  const [showStatusUpdateConfirmation, setShowStatusUpdateConfirmation] =
    useState(false);

  // ステータス更新ボタンの非活性判定
  const [isStatusUpdateButtonDisabled, setIsStatusUpdateButtonDisabled] =
    useState(false);

  useEffect(() => {
    if (reportInfo.reportStatus === reportDetail.status) {
      setIsStatusUpdateButtonDisabled(true);
    } else {
      setIsStatusUpdateButtonDisabled(false);
    }
  }, [reportInfo.reportStatus, reportDetail.status]);

  // ステータス変更ボタンクリック
  const handleUpdateReportStatusClick = () => {
    if (
      reportInfo.reportStatus === undefined ||
      reportInfo.reportStatus === ReportStatus.create ||
      reportInfo.reportStatus === reportDetail.status
    ) {
      return;
    }
    setShowStatusUpdateConfirmation(true);
  };

  const [showReportStatusConflictDialog, setShowReportStatusConflictDialog] =
    useState(false);

  // ステータス変更確認ダイアログの確認ボタンクリック
  const handleStatusChangeConfirmButtonClick = async () => {
    const resUpdate = await dispatch(
      updateReport(
        apiToken,
        { status: reportInfo.reportStatus },
        targetGroupId,
        reportInfo.reportId,
        reportInfo.updatedAt,
        SUCCESS_API_UPDATE_REPORT_STATUS,
      ),
    );
    if (resUpdate.status === 200) {
      showToast('ステータスを変更しました。');
      setIsStatusUpdateButtonDisabled(true);
    } else if (resUpdate.response.status === 412) {
      setShowReportStatusConflictDialog(true);
    } else if (
      resUpdate.response.data.errorCode ===
      REPORT_UPDATE_ERROR_CODE.UNEDITABLE_CONTENT
    ) {
      // すでに完了ステータスの場合、報告書プレビュー画面へ遷移する。
      dispatch(getReportDetail(apiToken, targetGroupId, reportInfo.reportId));
    }
    setShowStatusUpdateConfirmation(false);
  };

  const [selectMediaDialogMode, setSelectMediaDialogMode] = useState('');
  const [selectedUpdateImage, setSelectedUpdateImage] = useState({});
  const [selectedFrameInfo, setSelectedFrameInfo] = useState(null);
  // 写真選択ダイアログ表示
  const handleOpenSelectMediaDialog = (mode, frame) => {
    if (frame?.id === undefined) {
      // フレームがない場合は追加モード
      setSelectMediaDialogMode(SELECT_MEDIA_DIALOG_MODE.ADD);
    } else {
      setSelectMediaDialogMode(mode);
    }

    if (mode === SELECT_MEDIA_DIALOG_MODE.UPDATE) {
      setSelectedFrameInfo({
        id: frame?.id,
        updatedAt: frame?.updatedAt,
        note: frame?.note,
      });
      if (frame.workRecord === null) {
        setSelectedUpdateImage('noImage');
      } else {
        setSelectedUpdateImage(frame?.workRecord);
      }
    }
    setShowSelectMediaDialog(true);
  };

  const handleDeleteFrame = async (frameId, lastUpdatedAt) => {
    const res = await dispatch(
      deleteReportFrame(
        apiToken,
        targetGroupId,
        reportInfo.reportId,
        frameId,
        lastUpdatedAt,
      ),
    );
    if (res?.status === 200) {
      showToast(formatMessage({ id: 'toast.message.report.autoSave' }));
    }
    if (
      res?.response?.status === 400 &&
      res?.response?.data?.errorCode ===
        REPORT_UPDATE_ERROR_CODE.UNEDITABLE_CONTENT
    ) {
      await checkReportStatus(
        apiToken,
        targetGroupId,
        reportInfo.reportId,
        dispatch,
      );
    }
  };

  // 画像更新時に競合が発生した時に使用するstate
  const [conflictFrameId, setConflictFrameId] = useState(null);
  const [conflictFrameImage, setConflictFrameImage] = useState(null);

  // フレームが一つの場合、ゴミ箱ボタンを非表示にするためのフラグ
  const isSingleFrame = frames.length === 1;

  return (
    <MainBlock className="ReportEditPage">
      <TitleArea
        lastUpdatedAt={reportInfo.updatedAt ?? ''}
        reportStatus={reportInfo.reportStatus ?? ReportStatus.create}
        reportDetailStatus={reportDetail.status}
        reportStatusSelections={reportStatusSelections}
        onClickBack={() => {
          dispatch({ type: RESET_REPORT_DETAILS });
          history.push(PAGE_PATH.DS_REPORT);
        }}
        onClickPreview={() => history.push(`/ds/report/${reportInfo.reportId}`)}
        onChangeReportStatus={status =>
          setReportInfo({ ...reportInfo, reportStatus: status })
        }
        onClickChange={handleUpdateReportStatusClick}
        isStatusUpdateButtonDisabled={isStatusUpdateButtonDisabled}
        isAutoSaving={isAutoSaving}
      />
      <BodyBlock>
        {reportInfo?.reportId && (
          <Fragment key="ReportEditPage">
            <ReportTableWrapper>
              <EditArea
                reportNo={reportInfo.reportNo}
                subject={reportInfo.subject}
                createDate={reportInfo.createDate}
                author={reportInfo.author ?? ''}
                onChangeSubject={name => {
                  setReportInfo({ ...reportInfo, subject: name });
                  setReportTitle(name);
                }}
                onChangeCreateDate={value => {
                  setReportInfo({ ...reportInfo, createDate: value });
                  setCreateDate(convertDateToISO8601(value));
                }}
              />
              <FramesWrapper>
                {frames?.map((frame, index) => {
                  const key = `report-frame-${index}`;
                  return (
                    <Frame
                      id={key}
                      key={key}
                      reportId={reportInfo.reportId}
                      frame={frame}
                      onChangeNote={newNote => {
                        const newFrames = frames?.map((f, i) => {
                          if (i === index) {
                            return { ...f, note: newNote };
                          }
                          return f;
                        });
                        setFrames(newFrames);
                      }}
                      onDeleteFrame={() =>
                        handleDeleteFrame(frame.id, frame.updatedAt)
                      }
                      openSelectMediaDialog={() =>
                        handleOpenSelectMediaDialog(
                          SELECT_MEDIA_DIALOG_MODE.UPDATE,
                          frame,
                        )
                      }
                      setConflictFrameId={setConflictFrameId}
                      isSingleFrame={isSingleFrame}
                      updateAutoSaveStatus={updateAutoSaveStatus}
                    />
                  );
                })}
              </FramesWrapper>
              <PlusCircleButton
                onClick={() =>
                  handleOpenSelectMediaDialog(SELECT_MEDIA_DIALOG_MODE.ADD)
                }
              />
            </ReportTableWrapper>
          </Fragment>
        )}
      </BodyBlock>
      {/* ステータス変更確認ダイアログ */}
      {showStatusUpdateConfirmation && (
        <ConfirmationDialog
          headerText={statusUpdateConfirmationInfo.headerText}
          messageText={statusUpdateConfirmationInfo.messageText}
          onCancel={() => {
            setShowStatusUpdateConfirmation(false);
          }}
          onConfirm={handleStatusChangeConfirmButtonClick}
          confirmButtonText={statusUpdateConfirmationInfo.buttonText}
          confirmButtonColor={ColorSF2.navyBlue}
        />
      )}
      {/* 写真選択ダイアログ表示 */}
      {showSelectMediaDialog && (
        <SelectMediaDialog
          onClose={() => setShowSelectMediaDialog(false)}
          isGuest={isGuest(innerRole)}
          selectMediaDialogMode={selectMediaDialogMode}
          selectedUpdateImage={selectedUpdateImage}
          setSelectedUpdateImage={setSelectedUpdateImage}
          selectedFrameInfo={selectedFrameInfo}
          setConflictFrameId={setConflictFrameId}
          setConflictFrameImage={setConflictFrameImage}
          frames={frames}
        />
      )}
      {/* 報告書件名の編集時競合ダイアログ */}
      {showReportNameConflictDialog && (
        <ConfirmationDialog
          {...getDialogTexts(formatMessage)}
          // 上書き処理
          onConfirm={async () => {
            const res = await dispatch(
              getReportDetail(apiToken, targetGroupId, reportInfo.reportId),
            );
            dispatch(
              updateReport(
                apiToken,
                { name: debouncedTitle },
                targetGroupId,
                reportInfo.reportId,
                res?.data.updatedAt,
                SUCCESS_API_GET_REPORT_DETAIL,
              ),
            );
            setShowReportNameConflictDialog(false);
          }}
          // 破棄処理
          onSecondConfirm={() => {
            dispatch(
              getReportDetail(apiToken, targetGroupId, reportInfo.reportId),
            );
            setShowReportNameConflictDialog(false);
          }}
          confirmButtonText="上書き"
          secondConfirmButtonText="破棄"
          confirmButtonColor={ColorSF2.navyBlue}
          secondConfirmButtonColor={ColorSF2.red}
        />
      )}
      {/* 報告書作成日の編集時競合ダイアログ */}
      {showCreatedDateConflictDialog && (
        <ConfirmationDialog
          {...getDialogTexts(formatMessage)}
          // 上書き処理
          onConfirm={async () => {
            const res = await dispatch(
              getReportDetail(apiToken, targetGroupId, reportInfo.reportId),
            );
            dispatch(
              updateReport(
                apiToken,
                { createDate: debouncedDate },
                targetGroupId,
                reportInfo.reportId,
                res?.data.updatedAt,
                SUCCESS_API_GET_REPORT_DETAIL,
              ),
            );
            setShowCreatedDateConflictDialog(false);
          }}
          // 破棄処理
          onSecondConfirm={() => {
            dispatch(
              getReportDetail(apiToken, targetGroupId, reportInfo.reportId),
            );
            setShowCreatedDateConflictDialog(false);
          }}
          confirmButtonText="上書き"
          secondConfirmButtonText="破棄"
          confirmButtonColor={ColorSF2.navyBlue}
          secondConfirmButtonColor={ColorSF2.red}
        />
      )}
      {/* 報告書フレームの備考欄編集時競合ダイアログ */}
      {conflictFrameId && (
        <ConfirmationDialog
          {...getDialogTexts(formatMessage)}
          // 上書き処理
          onConfirm={async () => {
            const res = await dispatch(
              getReportDetail(apiToken, targetGroupId, reportInfo.reportId),
            );
            dispatch(
              updateReportFrame(
                apiToken,
                targetGroupId,
                reportInfo.reportId,
                conflictFrameId,
                {
                  note: frames.find(f => f.id === conflictFrameId)?.note,
                  freeTag: frames.find(f => f.id === conflictFrameId)?.freeTag,
                  tags: frames.find(f => f.id === conflictFrameId)?.tags,
                  workRecordId: frames.find(f => f.id === conflictFrameId)
                    ?.workRecord?.id,
                },
                res?.data.updatedAt,
              ),
            );
            setConflictFrameId(null);
          }}
          // 破棄処理
          onSecondConfirm={() => {
            dispatch(
              getReportDetail(apiToken, targetGroupId, reportInfo.reportId),
            );
            setConflictFrameId(null);
          }}
          confirmButtonText="上書き"
          secondConfirmButtonText="破棄"
          confirmButtonColor={ColorSF2.navyBlue}
          secondConfirmButtonColor={ColorSF2.red}
        />
      )}
      {/* 報告書フレームの写真編集時競合ダイアログ */}
      {conflictFrameImage && (
        <ConfirmationDialog
          {...getDialogTexts(formatMessage)}
          // 上書き処理
          onConfirm={async () => {
            const res = await dispatch(
              getReportDetail(apiToken, targetGroupId, reportInfo.reportId),
            );
            const requestBody = convertTimelineToApiParams(conflictFrameImage);
            dispatch(
              updateReportFrame(
                apiToken,
                targetGroupId,
                reportInfo.reportId,
                conflictFrameId,
                requestBody,
                res?.data.updatedAt,
              ),
            );
            setConflictFrameId(null);
            setConflictFrameImage(null);
          }}
          // 破棄処理
          onSecondConfirm={() => {
            dispatch(
              getReportDetail(apiToken, targetGroupId, reportInfo.reportId),
            );
            setConflictFrameId(null);
            setConflictFrameImage(null);
          }}
          confirmButtonText="上書き"
          secondConfirmButtonText="破棄"
          confirmButtonColor={ColorSF2.navyBlue}
          secondConfirmButtonColor={ColorSF2.red}
        />
      )}
      {/* ステータス変更競合ダイアログ */}
      {showReportStatusConflictDialog && (
        <ConfirmationDialog
          headerText={formatMessage({
            id: 'report.edit.conflictDialog.title',
          })}
          messageText={
            <>
              <FormattedMessage id="report.edit.statusConflictDialog.message1" />
              <br />
              <FormattedMessage id="report.edit.statusConflictDialog.message2" />
              <br />
              <FormattedMessage id="report.edit.statusConflictDialog.message3" />
            </>
          }
          // 上書き処理
          onConfirm={async () => {
            // TODO: 報告書一覧を取得しているため、一瞬他ユーザーの変更が表示されてしまうため要改善
            const res = await dispatch(
              getReportDetail(apiToken, targetGroupId, reportInfo.reportId),
            );
            dispatch(
              updateReport(
                apiToken,
                {
                  status: reportInfo.reportStatus,
                  name: reportInfo.subject,
                  createDate: reportInfo.updatedAt,
                },
                targetGroupId,
                reportInfo.reportId,
                res?.data.updatedAt,
                SUCCESS_API_GET_REPORT_DETAIL,
              ),
            );
            dispatch(
              updateReportFrame(
                apiToken,
                targetGroupId,
                reportInfo.reportId,
                conflictFrameId,
                {
                  note: frames.find(f => f.id === conflictFrameId)?.note,
                  freeTag: frames.find(f => f.id === conflictFrameId)?.freeTag,
                  tags: frames.find(f => f.id === conflictFrameId)?.tags,
                  workRecordId: frames.find(f => f.id === conflictFrameId)
                    ?.workRecord?.id,
                },
                res?.data.updatedAt,
              ),
            );
            setShowReportStatusConflictDialog(false);
          }}
          // 破棄処理
          onSecondConfirm={() => {
            dispatch(
              getReportDetail(apiToken, targetGroupId, reportInfo.reportId),
            );
            setShowReportStatusConflictDialog(false);
          }}
          confirmButtonText="上書き"
          secondConfirmButtonText="破棄"
          confirmButtonColor={ColorSF2.navyBlue}
          secondConfirmButtonColor={ColorSF2.red}
        />
      )}
    </MainBlock>
  );
};

export default ReportEditPage;
